/**
 * PermissionCenter — central UI for the new ABAC system (Phase 4).
 *
 * Tabs:
 *   1. Grants     — list/create/revoke per-user permission overrides
 *   2. Catalog    — read-only view of all permission codes + metadata
 *
 * Roles & Users management remain in their existing pages. This page
 * focuses on the NEW pieces: Grants and Catalog visibility.
 *
 * RBAC: gated by `admin_grant_list` for the page itself; create/revoke
 * actions are individually gated by PermissionShiftGate.
 */

import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Tabs,
  Tab,
  Paper,
  Typography,
  Chip,
  Button,
  TextField,
  MenuItem,
  IconButton,
  Stack,
  Alert,
  CircularProgress,
  Tooltip,
  TextField as MuiTextField,
  Autocomplete,
  Divider,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CancelIcon from '@mui/icons-material/Cancel';
import RefreshIcon from '@mui/icons-material/Refresh';
import LockIcon from '@mui/icons-material/Lock';
import StarIcon from '@mui/icons-material/Star';
import grantService, {
  type CreateGrantPayload,
  type GrantScope,
  type PermissionGrant,
} from '../services/grant.service';
import {
  PERMISSION_CATALOG,
  PermissionMeta,
  getAllDomains,
  getPermissionsByDomain,
} from '../constants/permissionCatalog';
import Gate from '../components/access/Gate';
import { CATEGORY_LABELS, type WorkCategoryCode } from '../services/workAssignment.service';
import {
  ResponsiveTable,
  ResponsiveModal,
  CopyableText,
} from '../components/common';
import type { Column } from '../components/common';

interface AdminLite {
  _id: string;
  name: string;
  email?: string;
  isSuperAdmin?: boolean;
}

const PermissionCenter: React.FC = () => {
  const [tab, setTab] = useState(0);

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h5" sx={{ mb: 2, fontWeight: 700 }}>
        مرکز مدیریت دسترسی‌ها
      </Typography>
      <Paper>
        <Tabs value={tab} onChange={(_, v) => setTab(v)} variant="fullWidth">
          <Tab label="دسترسی‌های موقت (Grant)" />
          <Tab label="کاتالوگ دسترسی‌ها" />
        </Tabs>
        <Divider />
        {tab === 0 && <GrantsTab />}
        {tab === 1 && <CatalogTab />}
      </Paper>
    </Box>
  );
};

// ---------------------------------------------------------------------------
// GRANTS TAB
// ---------------------------------------------------------------------------

const GrantsTab: React.FC = () => {
  const [items, setItems] = useState<PermissionGrant[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [limit, setLimit] = useState(20);
  const [activeOnly, setActiveOnly] = useState(true);
  const [loading, setLoading] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [revokeTarget, setRevokeTarget] = useState<PermissionGrant | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await grantService.list({
        active: activeOnly,
        page: page + 1,
        limit,
      });
      setItems(res.data || []);
      setTotal(res.pagination?.total ?? 0);
    } catch (e: any) {
      setError(e?.message || 'خطا در بارگذاری Grants');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, limit, activeOnly]);

  const columns: Column<PermissionGrant>[] = [
    {
      id: 'userId',
      label: 'کاربر',
      priority: 1,
      format: (_v, g) => (
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          {typeof g.userId === 'object' ? g.userId.name : String(g.userId).slice(-6)}
        </Typography>
      ),
    },
    {
      id: 'permission',
      label: 'دسترسی',
      priority: 2,
      format: (v) => <CopyableText value={v} mono maxWidth={220} />,
    },
    {
      id: 'category',
      label: 'حوزهٔ کاری',
      detail: true,
      format: (v) => (v ? CATEGORY_LABELS[v as WorkCategoryCode] || v : '—'),
    },
    {
      id: 'scope',
      label: 'مدت',
      priority: 3,
      format: (v) => (
        <Chip
          size="small"
          label={v === 'always' ? 'دائمی' : v === 'window' ? 'بازهٔ زمانی' : v === 'once' ? 'یک‌بار' : v}
          color={v === 'always' ? 'warning' : v === 'window' ? 'info' : 'default'}
        />
      ),
    },
    {
      id: 'reason',
      label: 'دلیل',
      detail: true,
      format: (v) => (
        <Tooltip title={v}>
          <Box
            component="span"
            sx={{
              display: 'inline-block',
              maxWidth: 200,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              verticalAlign: 'bottom',
            }}
          >
            {v}
          </Box>
        </Tooltip>
      ),
    },
    {
      id: 'grantedBy',
      label: 'اعطا شده توسط',
      detail: true,
      format: (_v, g) =>
        typeof g.grantedBy === 'object' ? g.grantedBy.name : String(g.grantedBy).slice(-6),
    },
    {
      id: 'expiresAt',
      label: 'انقضا',
      detail: true,
      format: (v) => (v ? new Date(v).toLocaleString('fa-IR') : '—'),
    },
    {
      id: 'isActive',
      label: 'وضعیت',
      priority: 4,
      format: (_v, g) =>
        g.isActive ? (
          <Chip size="small" label="فعال" color="success" />
        ) : g.consumedAt ? (
          <Chip size="small" label="مصرف‌شده" color="default" />
        ) : g.revokedAt ? (
          <Chip size="small" label="لغو شده" color="error" />
        ) : (
          <Chip size="small" label="منقضی" color="default" />
        ),
    },
  ];

  const renderActions = (g: PermissionGrant) =>
    g.isActive ? (
      <Gate permission="admin_grant_revoke" mode="hide">
        <Tooltip title="لغو Grant">
          <IconButton size="small" color="error" onClick={() => setRevokeTarget(g)}>
            <CancelIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Gate>
    ) : null;

  return (
    <Box sx={{ p: 2 }}>
      <Stack
        direction="row"
        spacing={2}
        alignItems="center"
        flexWrap="wrap"
        useFlexGap
        sx={{ mb: 2 }}
      >
        <Typography variant="subtitle1">
          {activeOnly ? 'Grants فعال' : 'همه‌ی Grants'}
        </Typography>
        <Button size="small" onClick={() => setActiveOnly((v) => !v)}>
          {activeOnly ? 'نمایش همه (شامل لغو/منقضی)' : 'فقط فعال‌ها'}
        </Button>
        <Box sx={{ flex: 1 }} />
        <IconButton onClick={() => void load()} disabled={loading}>
          <RefreshIcon />
        </IconButton>
        <Gate permission="admin_grant_create" mode="disable">
          <Button
            startIcon={<AddIcon />}
            variant="contained"
            onClick={() => setCreateOpen(true)}
          >
            اعطای Grant جدید
          </Button>
        </Gate>
      </Stack>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <ResponsiveTable<PermissionGrant>
        columns={columns}
        data={items}
        keyField="_id"
        loading={loading}
        emptyMessage="هیچ Grantی یافت نشد"
        renderActions={renderActions}
        pagination
        page={page}
        rowsPerPage={limit}
        totalCount={total}
        onPageChange={(p) => setPage(p)}
        onRowsPerPageChange={(rpp) => { setLimit(rpp); setPage(0); }}
        rowsPerPageOptions={[10, 20, 50]}
      />

      {createOpen && (
        <CreateGrantDialog
          open={createOpen}
          onClose={() => setCreateOpen(false)}
          onCreated={() => { setCreateOpen(false); void load(); }}
        />
      )}
      {revokeTarget && (
        <RevokeGrantDialog
          grant={revokeTarget}
          onClose={() => setRevokeTarget(null)}
          onRevoked={() => { setRevokeTarget(null); void load(); }}
        />
      )}
    </Box>
  );
};

// ---------------------------------------------------------------------------
// Create Grant dialog
// ---------------------------------------------------------------------------

const SCOPE_OPTIONS: { value: GrantScope; label: string; hint: string }[] = [
  { value: 'window', label: 'بازهٔ زمانی', hint: 'فقط بین «از» و «تا» معتبر است؛ بعد از پایان بازه خودکار غیرفعال می‌شود.' },
  { value: 'once', label: 'یک‌بار مصرف', hint: 'با اولین استفاده مصرف می‌شود (حداکثر ۳۰ روز اعتبار).' },
  { value: 'always', label: 'دائمی تا لغو', hint: 'تا زمانی که دستی لغو شود یا ۳۰ روز بگذرد معتبر است — محتاطانه استفاده کنید.' },
];

/** datetime-local value in the BROWSER's local zone (not UTC — toISOString() was 3.5h off in Tehran). */
const toLocalInput = (d: Date): string => {
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
};

const WINDOW_PRESETS: { label: string; hours: number | 'today' }[] = [
  { label: 'تا پایان امروز', hours: 'today' },
  { label: '۲۴ ساعت', hours: 24 },
  { label: '۳ روز', hours: 72 },
  { label: '۷ روز', hours: 24 * 7 },
];

const CreateGrantDialog: React.FC<{
  open: boolean;
  onClose: () => void;
  onCreated: () => void;
}> = ({ open, onClose, onCreated }) => {
  const [admins, setAdmins] = useState<AdminLite[]>([]);
  const [user, setUser] = useState<AdminLite | null>(null);
  const [mode, setMode] = useState<'single' | 'category'>('single');
  const [permission, setPermission] = useState<string>('');
  const [category, setCategory] = useState<WorkCategoryCode | ''>('');
  const [scope, setScope] = useState<GrantScope>('window');
  const [from, setFrom] = useState<string>('');
  const [to, setTo] = useState<string>('');
  const [reason, setReason] = useState<string>('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // Use the new dedicated /admin/grants/admins endpoint so we see ALL active
    // admins, not just those eligible for ticket assignment (which was the
    // old behavior and the reason only super-admins showed up).
    (async () => {
      try {
        const list = await grantService.listAdmins();
        setAdmins(list);
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('Failed to load admin list for grant picker:', err);
      }
    })();
    // default window: now → +24h (local time)
    const now = new Date();
    setFrom(toLocalInput(now));
    setTo(toLocalInput(new Date(now.getTime() + 24 * 3600_000)));
  }, []);

  const applyPreset = (hours: number | 'today') => {
    const now = new Date();
    const end = new Date(now);
    if (hours === 'today') end.setHours(23, 59, 0, 0);
    else end.setTime(now.getTime() + hours * 3600_000);
    setFrom(toLocalInput(now));
    setTo(toLocalInput(end));
  };

  const allPermCodes = useMemo(() => Object.keys(PERMISSION_CATALOG).sort(), []);
  const categoryCodes = useMemo(() => Object.keys(CATEGORY_LABELS) as WorkCategoryCode[], []);
  const permMeta = permission ? (PERMISSION_CATALOG as Record<string, PermissionMeta>)[permission] : undefined;
  const effectiveCategory: string | null =
    mode === 'category' ? category || null : permMeta?.category || null;
  const categoryPermCount = useMemo(
    () => (category ? allPermCodes.filter((c) => (PERMISSION_CATALOG as Record<string, PermissionMeta>)[c].category === category).length : 0),
    [category, allPermCodes]
  );

  const submit = async () => {
    setError(null);
    if (!user) return setError('کاربر را انتخاب کنید');
    if (mode === 'single' && !permission) return setError('دسترسی را انتخاب کنید');
    if (mode === 'category' && !category) return setError('حوزهٔ کاری را انتخاب کنید');
    if (reason.trim().length < 5) return setError('دلیل باید حداقل ۵ کاراکتر باشد');
    if (scope === 'window') {
      if (!from || !to) return setError('بازهٔ «از» و «تا» لازم است');
      if (new Date(from) >= new Date(to)) return setError('«تا» باید بعد از «از» باشد');
      if (new Date(to).getTime() - Date.now() > 90 * 24 * 3600_000) return setError('حداکثر مدت گرنت ۹۰ روز است');
    }

    setSubmitting(true);
    try {
      const payload: CreateGrantPayload = {
        userId: user._id,
        permission: mode === 'category' ? '*' : permission,
        ...(mode === 'category' ? { category: category as WorkCategoryCode } : {}),
        scope,
        reason: reason.trim(),
        ...(scope === 'window'
          ? { window: { from: new Date(from).toISOString(), to: new Date(to).toISOString() } }
          : {}),
      };
      await grantService.create(payload);
      onCreated();
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || 'خطا در ساخت گرنت');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <ResponsiveModal
      open={open}
      onClose={onClose}
      title="اعطای دسترسی موقت"
      maxWidth="sm"
      error={error}
      disableClose={submitting}
      actions={
        <>
          <Button onClick={onClose}>انصراف</Button>
          <Button variant="contained" onClick={submit} disabled={submitting}>
            {submitting ? <CircularProgress size={20} /> : 'اعطا'}
          </Button>
        </>
      }
    >
      <Stack spacing={2} sx={{ mt: 1 }}>
        <Autocomplete
          options={admins}
          value={user}
          onChange={(_, v) => setUser(v)}
          getOptionLabel={(a) => `${a.name}${a.email ? ` (${a.email})` : ''}`}
          isOptionEqualToValue={(a, b) => a._id === b._id}
          renderInput={(p) => (
            <MuiTextField
              {...p}
              label={`ادمین مقصد — ${admins.length} ادمین فعال`}
              placeholder="جستجو در نام یا ایمیل..."
            />
          )}
          renderOption={(props, opt) => (
            <li {...props} key={opt._id}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="body2" noWrap>{opt.name}</Typography>
                  {opt.email && (
                    <Typography variant="caption" color="text.secondary" noWrap>
                      {opt.email}
                    </Typography>
                  )}
                </Box>
                {opt.isSuperAdmin && (
                  <Chip
                    size="small"
                    label="سوپر-ادمین"
                    color="warning"
                    sx={{ fontSize: 10, height: 20, fontWeight: 600 }}
                  />
                )}
              </Box>
            </li>
          )}
        />

        <TextField select label="نوع دسترسی" value={mode} onChange={(e) => setMode(e.target.value as 'single' | 'category')}>
          <MenuItem value="single">یک دسترسی مشخص</MenuItem>
          <MenuItem value="category">همهٔ دسترسی‌های یک حوزهٔ کاری</MenuItem>
        </TextField>

        {mode === 'single' ? (
          <Autocomplete
            options={allPermCodes}
            value={permission || null}
            onChange={(_, v) => setPermission(v || '')}
            getOptionLabel={(opt) => {
              const meta = (PERMISSION_CATALOG as Record<string, PermissionMeta>)[opt];
              return meta ? `${meta.nameFa} — ${opt}` : opt;
            }}
            renderInput={(p) => <MuiTextField {...p} label="دسترسی" placeholder="جستجو با نام فارسی یا کد..." />}
            renderOption={(props, opt) => {
              const meta = (PERMISSION_CATALOG as Record<string, PermissionMeta>)[opt];
              return (
                <li {...props} key={opt}>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="body2" noWrap>
                      {meta?.nameFa}
                      {meta?.category && (
                        <Chip size="small" label={CATEGORY_LABELS[meta.category as WorkCategoryCode] || meta.category} color="info" sx={{ ml: 1, height: 18, fontSize: 10 }} />
                      )}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      <code>{opt}</code> · {meta?.domain}
                    </Typography>
                  </Box>
                </li>
              );
            }}
          />
        ) : (
          <TextField select label="حوزهٔ کاری" value={category} onChange={(e) => setCategory(e.target.value as WorkCategoryCode)}>
            {categoryCodes.map((c) => (
              <MenuItem key={c} value={c}>{CATEGORY_LABELS[c]}</MenuItem>
            ))}
          </TextField>
        )}

        {effectiveCategory && (
          <Alert severity="info" sx={{ py: 0.5 }}>
            {mode === 'category'
              ? `${categoryPermCount} دسترسی حوزهٔ «${CATEGORY_LABELS[effectiveCategory as WorkCategoryCode] || effectiveCategory}» را می‌دهد. `
              : `این دسترسی به حوزهٔ «${CATEGORY_LABELS[effectiveCategory as WorkCategoryCode] || effectiveCategory}» تعلق دارد. `}
            در مدت اعتبار گرنت، محدودیت شیفت و «اختصاص حوزه» برای این حوزه اعمال نمی‌شود؛ ادمین می‌تواند خارج از شیفت هم روی این کارها اقدام کند.
          </Alert>
        )}

        <TextField select label="مدت اعتبار" value={scope} onChange={(e) => setScope(e.target.value as GrantScope)}
          helperText={SCOPE_OPTIONS.find((s) => s.value === scope)?.hint}
        >
          {SCOPE_OPTIONS.map((s) => (
            <MenuItem key={s.value} value={s.value}>{s.label}</MenuItem>
          ))}
        </TextField>

        {scope === 'window' && (
          <>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              {WINDOW_PRESETS.map((p) => (
                <Chip key={p.label} label={p.label} size="small" variant="outlined" onClick={() => applyPreset(p.hours)} />
              ))}
            </Stack>
            <Stack direction="row" spacing={1}>
              <TextField
                fullWidth
                type="datetime-local"
                label="از"
                value={from}
                onChange={(e) => setFrom(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                fullWidth
                type="datetime-local"
                label="تا"
                value={to}
                onChange={(e) => setTo(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Stack>
          </>
        )}
        <TextField
          label="دلیل (برای ممیزی؛ برای ادمین مقصد هم نمایش داده می‌شود)"
          multiline
          minRows={2}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
        />
      </Stack>
    </ResponsiveModal>
  );
};

// ---------------------------------------------------------------------------
// Revoke Grant dialog
// ---------------------------------------------------------------------------

const RevokeGrantDialog: React.FC<{
  grant: PermissionGrant;
  onClose: () => void;
  onRevoked: () => void;
}> = ({ grant, onClose, onRevoked }) => {
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    if (reason.trim().length < 3) {
      setError('دلیل لازم است (حداقل ۳ کاراکتر)');
      return;
    }
    setSubmitting(true);
    try {
      await grantService.revoke(grant._id, reason.trim());
      onRevoked();
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || 'خطا در لغو Grant');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <ResponsiveModal
      open
      onClose={onClose}
      title="لغو Grant"
      maxWidth="xs"
      error={error}
      disableClose={submitting}
      actions={
        <>
          <Button onClick={onClose}>انصراف</Button>
          <Button color="error" variant="contained" onClick={submit} disabled={submitting}>
            {submitting ? <CircularProgress size={20} /> : 'لغو فوری'}
          </Button>
        </>
      }
    >
      <Stack spacing={2} sx={{ mt: 1 }}>
        <Typography variant="body2">
          دسترسی: <code>{grant.permission}</code>
        </Typography>
        <Typography variant="body2">
          کاربر: {typeof grant.userId === 'object' ? grant.userId.name : String(grant.userId).slice(-6)}
        </Typography>
        <TextField
          label="دلیل لغو (audit)"
          multiline
          minRows={2}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
        />
      </Stack>
    </ResponsiveModal>
  );
};

// ---------------------------------------------------------------------------
// CATALOG TAB (read-only)
// ---------------------------------------------------------------------------

interface CatalogRow {
  code: string;
  meta: PermissionMeta;
}

const CatalogTab: React.FC = () => {
  const [filter, setFilter] = useState('');
  const domains = useMemo(() => getAllDomains(), []);

  const filteredEntries = useMemo(() => {
    const out: { domain: string; codes: string[] }[] = [];
    const f = filter.trim().toLowerCase();
    for (const dom of domains) {
      const codes = getPermissionsByDomain(dom).filter((c) => {
        if (!f) return true;
        const meta = (PERMISSION_CATALOG as Record<string, PermissionMeta>)[c];
        return c.toLowerCase().includes(f) || meta?.nameFa?.includes(f);
      });
      if (codes.length > 0) out.push({ domain: dom, codes });
    }
    return out;
  }, [filter, domains]);

  const columns: Column<CatalogRow>[] = [
    {
      id: 'code',
      label: 'کد',
      priority: 1,
      format: (v) => <CopyableText value={v} mono maxWidth={220} />,
    },
    {
      id: 'nameFa',
      label: 'عنوان فارسی',
      priority: 2,
      format: (_v, row) => row.meta.nameFa,
    },
    {
      id: 'action',
      label: 'action',
      priority: 3,
      format: (_v, row) => (
        <Chip
          size="small"
          label={row.meta.action}
          color={row.meta.action === 'admin' ? 'error' : row.meta.action === 'write' ? 'warning' : 'default'}
        />
      ),
    },
    {
      id: 'shift',
      label: 'shift',
      priority: 4,
      format: (_v, row) =>
        row.meta.shiftPolicy === 'enforce' ? (
          <Chip size="small" label={row.meta.category || 'gated'} color="info" />
        ) : (
          <Chip size="small" label="cross-cutting" />
        ),
    },
    {
      id: 'danger',
      label: 'danger',
      detail: true,
      format: (_v, row) => (
        <Chip
          size="small"
          label={row.meta.dangerLevel}
          color={row.meta.dangerLevel === 'high' ? 'error' : row.meta.dangerLevel === 'medium' ? 'warning' : 'default'}
        />
      ),
    },
    {
      id: 'flags',
      label: 'flags',
      detail: true,
      format: (_v, row) => (
        <Stack direction="row" spacing={0.5}>
          {row.meta.secure && (
            <Tooltip title="حساس — مشاهده نیز خارج از شیفت ممنوع">
              <LockIcon fontSize="small" color="error" />
            </Tooltip>
          )}
          {row.meta.requireSuperAdmin && (
            <Tooltip title="فقط سوپر-ادمین">
              <StarIcon fontSize="small" color="warning" />
            </Tooltip>
          )}
          {/* `legacy` field removed Session 4 cutover. */}
        </Stack>
      ),
    },
    {
      id: 'dependencies',
      label: 'dependencies',
      detail: true,
      format: (_v, row) =>
        row.meta.dependencies?.length ? (
          <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
            {row.meta.dependencies.map((d) => (
              <Chip key={d} size="small" variant="outlined" label={d} sx={{ fontSize: 10 }} />
            ))}
          </Stack>
        ) : (
          '—'
        ),
    },
  ];

  return (
    <Box sx={{ p: 2 }}>
      <Stack direction="row" spacing={2} sx={{ mb: 2 }} alignItems="center">
        <TextField
          placeholder="جستجو در دسترسی‌ها..."
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          size="small"
          fullWidth
        />
        <Typography variant="caption" color="text.secondary">
          {Object.keys(PERMISSION_CATALOG).length} دسترسی · {domains.length} دامنه
        </Typography>
      </Stack>

      {filteredEntries.map(({ domain, codes }) => (
        <Box key={domain} sx={{ mb: 3 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
            {domain}
          </Typography>
          <ResponsiveTable<CatalogRow>
            columns={columns}
            data={codes.map((c) => ({
              code: c,
              meta: (PERMISSION_CATALOG as Record<string, PermissionMeta>)[c],
            }))}
            keyField="code"
            emptyMessage="دسترسی‌ای یافت نشد"
          />
        </Box>
      ))}
    </Box>
  );
};

export default PermissionCenter;
